/**
 * ConditionEvaluator - base class for behavior tree conditions. Checks game
 * state and returns Success or Failure.
 */

import { BehaviorStatus } from '../BehaviorNode.js';

import type { Actor } from '../../../actors/Actor.js';
import type { Blackboard } from '../blackboard/Blackboard.js';

export interface ConditionEvaluatorOptions {
  name?: string;
  description?: string;
  invert?: boolean;
}

/** Base class for condition evaluators. Checks game state and returns Success/Failure. */
export abstract class ConditionEvaluator {
  public readonly name: string;
  public readonly description: string;
  protected readonly invert: boolean;
  private isInitialized: boolean = false;

  constructor(options: ConditionEvaluatorOptions = {}) {
    this.name = options.name ?? this.constructor.name;
    this.description = options.description ?? '';
    this.invert = options.invert ?? false;
  }

  public initialize(blackboard: Blackboard): void {
    if (!this.isInitialized) {
      this.onInitialize(blackboard);
      this.isInitialized = true;
    }
  }

  public async evaluate(blackboard: Blackboard): Promise<BehaviorStatus> {
    if (!this.isInitialized) {
      this.initialize(blackboard);
    }

    const result = await this.onEvaluate(blackboard);

    // Apply inversion if requested
    if (this.invert) {
      return result === BehaviorStatus.Success ? BehaviorStatus.Failure : BehaviorStatus.Success;
    }

    return result;
  }

  public reset(): void {
    this.onReset();
  }

  public destroy(): void {
    this.onDestroy();
  }

  protected getOwner(blackboard: Blackboard): Actor | null {
    return blackboard.getOwner();
  }

  protected getWorld(blackboard: Blackboard) {
    return this.getOwner(blackboard)?.getWorld();
  }

  protected onInitialize(blackboard: Blackboard): void {}

  protected abstract onEvaluate(blackboard: Blackboard): Promise<BehaviorStatus>;

  protected onReset(): void {}

  protected onDestroy(): void {}

  public getDebugInfo(): { name: string; description: string } {
    return {
      name: this.name,
      description: this.description
    };
  }
}